import { extendTheme, type ThemeConfig } from "@chakra-ui/react"
import { ButtonTextStyles } from "./ButtonText"
import { Colors } from "./colors"
import { loginCheckboxTheme } from "./loginCheckboxTheme"
import { modalTheme } from "./modalTheme"

const config: ThemeConfig = {
  initialColorMode: "light",
  useSystemColorMode: false
}

const breakpoints = {
  base: "0em", // 0px
  sm: "30em", // ~480px. em is a relative unit and is dependant on the font-size.
  md: "51em", // ~768px
  lg: "62em", // ~992px
  xl: "80em", // ~1280px
  "2xl": "96em" // ~1536px
}
const RHeading1 = {
  fontFamily: "Work Sans",
  fontSize: "40px",
  fontStyle: "normal",
  fontWeight: 600,
  lineHeight: "40px"
}
const Heading1 = {
  fontFamily: "Work Sans",
  lineHeight: ["40px", "auto", "auto", "66px", "68px"],
  fontSize: ["30px", "30px", "30px", "60px", "60px", "60px"],
  fontStyle: "normal"
}

const Heading3PT = {
  fontFamily: "PT Sans",
  fontSize: ["16px", "16px", "16px", "22.5px", "22.5px", "22.5px"],
  lineHeight: ["auto", "auto", "auto", "26px", "26px", "26px"]
}
const RHeading3PT = {
  fontFamily: "PT Sans",
  fontSize: "24px",
  fontWeight: 400,
  lineHeight: "32px"
}
const RHeading4Bold = {
  fontFamily: "PT Sans",
  fontSize: "20px",
  fontWeight: 700,
  lineHeight: "24px"
}
const RHeading4 = {
  fontFamily: "PT Sans",
  fontSize: "20px",
  fontWeight: 400,
  lineHeight: "24px"
}

const RButton2 = {
  fontFamily: "PT Sans",
  fontSize: "16px",
  fontStyle: "normal",
  fontWeight: 400,
  lineHeight: "24px"
}
const RBodyBold = {
  fontFamily: "PT Sans",
  fontSize: "16px",
  fontStyle: "normal",
  fontWeight: 700,
  lineHeight: "24px"
}
const rBody = {
  fontFamily: "PT Sans",
  fontSize: "16px",
  fontStyle: "normal",
  fontWeight: 400,
  lineHeight: "24px"
}
const rHeading2 = {
  fontFamily: "Work Sans",
  fontSize: ["24px", "24px", "24px", "36px"],
  fontWeight: "600",
  lineHeight: ["28px", "28px", "28px", "42px"]
}
export const CheckBoxText = {
  fontFamily: "PT Sans",
  fontSize: "15px",
  fontStyle: "normal",
  fontWeight: 400,
  lineHeight: "19px"
}
export const chakraTheme = extendTheme({
  ...config,
  breakpoints,
  global: {
    "html, body": {
      fontFamily: "PT Sans, sans-serif",
      color: "red"
    }
  },
  body: {
    fontFamily: "PT Sans, sans-serif",
    bg: "red"
  },
  fonts: {
    heading: "PT Sans, sans-serif",
    body: "PT Sans, sans-serif",
    button: "PT Sans, 'sans-serif'",
    text: "PT Sans, 'sans-serif'"
  },
  colors: {
    primary: "#861F41",
    primary_hover: "#A41C4910",
    accent: "#AB8476",
    accent50: "#AB847650",
    teal: "#0B7189",
    lightGray: "#F4F4F5",
    primaryC250: "#93959805",
    grayE: "#EEE",
    grayLighter: "#F7F7F7",
    brown: "#AB8476",
    blackText: "#231F20",
    grayF5: "#F5F5F5",
    gray: {
      100: "#939598",
      50: "#93959850",
      35: "#93959835"
    },
    inputBg: "#EDDDE2",
    accent2: "#939598",
    cardBgLight: "white",
    cardBgDark: "gray.700",
    cardBgImageLight: "#f7f7f7",
    cardBgImageDark: "gray.500",
    tableHeadingGray: "#C9CACB",
    imageFallBackBackground: "#f9f5f4",
    accent3: {
      50: "#AB847650",
      100: "#AB847670",
      500: "#AB8476"
    }
  },
  components: {
    Modal: modalTheme,
    Button: {
      baseStyle: {
        backgroundColor: Colors.Primary.c1,
        color: Colors.Primary.white,
        borderRadius: "25px",
        display: "inline-block",
        minHeight: "auto"
      },
      variants: {
        dropdownMenuButton: {
          ...ButtonTextStyles.Button1Regular,
          fontFamily: "Work Sans",
          width: "100%",
          display: "flex",
          bg: Colors.Primary.c1,
          minWidth: "133px",
          justifyContent: "space-between",
          color: "white",
          borderRadius: "0",
          height: "34px",
          fontWeight: 500,
          _hover: {
            backgroundColor: Colors.Alternative.accent
          }
        },
        "ButtonPrimary-xl": {
          boxShadow: "0px 3px 7px 0px rgba(0, 0, 0, 0.25)",
          padding: "7px 21px",
          ...ButtonTextStyles.Button1Big,
          height: "auto"
        },
        Button1Regular: {
          ...ButtonTextStyles.Button1Regular,
          outline: `1px solid ${Colors.Primary.c1}`,
          padding: "6px 18px",
          height: "auto",
          _hover: {
            bg: "#6e1835"
          }
        },
        Button1RegularWithShadow: {
          ...ButtonTextStyles.Button1Regular,
          outline: `1px solid ${Colors.Primary.c1}`,
          padding: "6px 18px",
          height: "auto",
          boxShadow: "0px 3px 5px 0px rgba(0, 0, 0, 0.25)",
          _hover: {
            bg: "#6e1835"
          }
        },
        Button1RegularOutline: {
          ...ButtonTextStyles.Button1Regular,
          backgroundColor: Colors.Primary.white,
          color: Colors.Primary.c1,
          outline: `1px solid ${Colors.Primary.c1}`,
          height: "auto",
          // padding: '6px 18px',
          padding: "5px 25px",
          boxShadow: "0px 3px 5px 0px rgba(0, 0, 0, 0.25)"
        },
        Button1RegularOutline1: {
          ...ButtonTextStyles.Button1Regular,
          backgroundColor: Colors.Primary.white,
          color: Colors.Primary.c1,
          outline: `1px solid ${Colors.Primary.c1}`,
          padding: "6px 24px",
          borderRadius: "25px",
          // boxShadow: '0px 3px 5px 0px rgba(0, 0, 0, 0.25)',
          border: "1px solid #861F41"
        },
        loginButton: {
          ...ButtonTextStyles.Button1Regular,
          backgroundColor: Colors.Primary.white,
          color: Colors.Primary.c1,
          height: "19px",
          padding: "5px 25px",
          boxSizing: "content-box"
        },
        Button1RegularAccent: {
          ...ButtonTextStyles.Button1Regular,
          backgroundColor: Colors.Alternative.accent,
          outline: `1px solid ${Colors.Alternative.accent}`,
          height: "auto",
          padding: "6px 18px"
        },
        Button1RegularAccentWithShadow: {
          ...ButtonTextStyles.Button1Regular,
          backgroundColor: Colors.Alternative.accent,
          outline: `1px solid ${Colors.Alternative.accent}`,
          height: "auto",
          padding: "6px 18px",
          boxShadow: "0px 3px 5px 0px rgba(0, 0, 0, 0.25)"
        },
        Button1RegularAmber: {
          ...ButtonTextStyles.Button1Regular,
          backgroundColor: "#D97706",
          outline: "1px solid #D97706",
          height: "auto",
          padding: "6px 18px",
          _hover: {
            bg: "#B45309"
          }
        },
        Button1Small: {
          padding: "5px 15px",
          ...ButtonTextStyles.Button1Small,
          height: "auto"
        },
        RCaptionBold: {
          fontFamily: "PT Sans",
          fontSize: "12px",
          fontStyle: "normal",
          fontWeight: "700",
          lineHeight: "16px"
        },
        Button3Work: {
          ...ButtonTextStyles.Button3Work,
          height: "auto",
          padding: "4px 12px"
        },
        linkButton: {
          ...ButtonTextStyles.Button2Bold,
          p: "0",
          borderRadius: 0,
          height: "auto",
          backgroundColor: "transparent",
          color: Colors.Primary.c1,
          _hover: {
            textDecoration: "underline"
          }
        },
        linkButtonBig: {
          ...ButtonTextStyles.Button2Big,
          p: "0",
          borderRadius: 0,
          height: "auto",
          backgroundColor: "transparent",
          color: Colors.Primary.c1,
          _hover: {
            textDecoration: "underline"
          }
        },
        linkButtonButton1BigPT: {
          ...ButtonTextStyles.Button1BigPT,
          p: "0",
          borderRadius: 0,
          height: "auto",
          backgroundColor: "transparent",
          color: Colors.Primary.c1,
          _hover: {
            textDecoration: "none"
          }
        },
        Button1CAPS: {
          ...ButtonTextStyles.Button1CAPS,
          height: "auto"
        },
        RBodyBold: {
          ...RBodyBold
        },
        RBody: {
          ...rBody
        },
        RButton: {
          fontFamily: "Work Sans",
          fontSize: "16px",
          fontStyle: "normal",
          fontWeight: 400,
          lineHeight: "20px",
          borderRadius: "15px",
          textTransform: "capitalize",
          padding: "6px 18px"
        },
        RButtonBold: {
          fontFamily: "Work Sans",
          fontSize: "16px",
          fontStyle: "normal",
          fontWeight: 500,
          lineHeight: "20px",
          borderRadius: "15px",
          textTransform: "capitalize",
          padding: "6px 18px"
        },
        RButton2: {
          fontFamily: "PT Sans",
          fontSize: "16px",
          fontStyle: "normal",
          fontWeight: "400",
          lineHeight: "24px",
          textTransform: "capitalize",
          backgroundColor: "white",
          color: Colors.Primary.c1
        },
        "ButtonOutline-icon": {
          padding: "5px 16px",
          alignItems: "center",
          borderRadius: "10px",
          border: "1px solid #861F41",
          lineHeight: "19px",
          height: "auto",
          backgroundColor: "white",
          color: "primary",
          fontWeight: 400,
          _hover: {
            bg: "#861F4110"
          }
        }
      }
    },
    Checkbox: loginCheckboxTheme,
    Text: {
      baseStyle: {
        fontFamily: "PT Sans"
      },
      variants: {
        Body1Big: {
          fontWeight: 400,
          fontSize: "16px",
          lineHeight: "20px"
        },
        Body1Regular: {
          fontWeight: 400,
          fontSize: ["16px", "16px", "16px", "15px"],
          lineHeight: ["24px", "24px", "24px", "19px"]
        },
        Body1Bold: {
          fontWeight: 700,
          fontSize: ["15px", "15px", "15px", "15px"],
          lineHeight: ["24px", "24px", "24px", "20px"]
        },
        RBody: {
          ...rBody
        },
        RBodyBold: {
          ...RBodyBold
        },
        Body1Italic: {
          fontWeight: 400,
          fontSize: "15px",
          lineHeight: ["24px", "24px", "24px", "20px"],
          fontStyle: "italic"
        },
        Body1Small: {
          fontWeight: 400,
          fontSize: "14px",
          lineHeight: ["22px", "22px", "22px", "17px"]
        },
        Body2Bold: {
          fontWeight: 700,
          fontSize: "11px",
          lineHeight: ["auto", "auto", "auto", "11px"]
        },

        Body2Regular: {
          fontWeight: 400,
          fontSize: "11px",
          lineHeight: ["auto", "auto", "auto", "11px"]
        },
        caption: {
          fontSize: "12px",
          lineHeight: ["auto", "auto", "auto", "15px"],
          fontWeight: 400
        },
        captionBold: {
          fontSize: "12px",
          lineHeight: ["auto", "auto", "auto", "15px"],
          fontWeight: 700
        },
        captionWork: {
          fontFamily: "Work Sans",
          fontSize: "12px",
          lineHeight: ["auto", "auto", "auto", "15px"],
          fontWeight: 700
        },
        captionSmall: {
          fontSize: "10px",
          lineHeight: ["auto", "auto", "auto", "13px"],
          fontWeight: 400
        },
        caption2Small: {
          fontSize: "9px",
          lineHeight: ["auto", "auto", "auto", "11px"],
          fontWeight: 400
        },
        Heading3PT: {
          ...Heading3PT
        },
        RHeading1: {
          ...RHeading1
        },
        RHeading3PT: {
          ...RHeading3PT
        },
        RButton2: {
          ...RButton2
        },

        termsConditionaPara: {
          ...rBody,
          color: "blackText"
        },
        RHeading4Bold: {
          ...RHeading4Bold
        },
        RHeading4: {
          ...RHeading4
        },
        RCaption: {
          fontFamily: "PT Sans",
          fontSize: "12px",
          fontStyle: "normal",
          fontWeight: "400",
          lineHeight: "16px"
        },
        RCaptionBold: {
          fontFamily: "PT Sans",
          fontSize: "12px",
          fontStyle: "normal",
          fontWeight: "700",
          lineHeight: "16px"
        },
        RHeading3Bold: {
          fontFamily: "PT Sans",
          fontSize: "20px",
          fontStyle: "normal",
          fontWeight: "700",
          lineHeight: "24px"
        }
      }
    },
    BreadCrumb: {
      baseStyle: {
        color: "primary",
        fontFamily: "PT Sans",
        fontSize: "12px",
        lineHeight: ["auto", "auto", "auto", "15px"],
        fontWeight: 400
      }
    },
    Heading: {
      baseStyle: {
        fontFamily: "Work Sans"
      },
      variants: {
        RHeading1: {
          ...RHeading1
        },
        Heading1: {
          ...Heading1,
          fontWeight: "600"
        },
        Heading1SemiBold: {
          ...Heading1,
          fontWeight: "600"
        },
        Heading1Medium: {
          ...Heading1,
          fontWeight: "500"
        },
        Heading2SemiBold: { ...rHeading2 },
        RHeading2: {
          ...rHeading2
        },
        Heading3BoldPT: {
          ...Heading3PT,
          fontWeight: "700"
        },
        Heading3PT: {
          ...Heading3PT,
          fontWeight: "400"
        },
        Heading3Work: {
          ...Heading3PT,
          fontFamily: "Work Sans",
          fontWeight: "500"
        },
        RHeading3Work: {
          fontFamily: "Work Sans",
          fontSize: "16px",
          fontStyle: "normal",
          fontWeight: "500",
          lineHeight: "20px",
          textTransform: "capitalize"
        },
        RBodyBold: {
          ...RBodyBold
        },
        Heading3CAPS: {
          ...Heading3PT,
          fontFamily: "Work Sans",
          textTransform: "uppercase",
          fontWeight: "500"
        },
        RHeading3CAPS: {
          fontSize: "24px",
          lineHeight: "24px",
          fontFamily: "Work Sans",
          textTransform: "uppercase",
          fontWeight: "500"
        },
        Heading4: {
          fontFamily: "PT Sans",
          fontSize: "20px",
          fontWeight: 700,
          lineHeight: "24px"
        },
        RHeading3PT: {
          ...RHeading3PT
        },
        RBody: {
          ...rBody
        },
        Heading5: {
          fontFamily: "PT Sans",
          fontSize: "20px !important",
          fontWeight: 700,
          lineHeight: "24px"
        },
        Heading5Italic: {
          fontFamily: "PT Sans",
          fontSize: "20px !important",
          fontStyle: "italic",
          fontWeight: 700,
          lineHeight: "23px"
        },
        Heading6Bold: {
          fontFamily: "PT Sans",
          fontSize: "18px",
          fontWeight: 700,
          lineHeight: "18px"
        },
        Heading6Regular: {
          fontFamily: "PT Sans",
          fontSize: "18px",
          fontWeight: 400,
          lineHeight: "18px"
        },
        RButton2: {
          ...RButton2
        },
        RHeading4: {
          ...RHeading4Bold,
          fontWeight: 400
        }
      }
    }
  }
})
